// The changes Policy chat offers, as fixed step plans.
//
// A change lists its steps in order. Each step names the card that asks it,
// the answer shape that card produces, and the allowed values. `writes` turns
// the answers into one agent's settings, starting from that agent's current
// signed policy. The chat model never writes settings: it records answers and
// the page calls `writes`. Every document `writes` can produce is checked by
// the shared validator through `__fixtures__/policyChatDocuments.json`.
import { valueAt } from "../catalogDocument.js";

export const CLAUDE = "claude-code";
export const CODEX = "codex";
export const COPILOT = "github-copilot";
export const AGENT_IDS = Object.freeze([CLAUDE, CODEX, COPILOT]);
export const AGENT_LABELS = Object.freeze({ [CLAUDE]: "Claude Code", [CODEX]: "Codex", [COPILOT]: "GitHub Copilot" });

const unique = (values) => {
    const seen = new Set();
    return values.filter((value) => {
        const key = JSON.stringify(value);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};
const sameList = (a, b) => JSON.stringify(a ?? []) === JSON.stringify(b ?? []);
const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const marked = (map, decision) => Object.keys(map ?? {}).filter((key) => map[key] === decision);
/// A change for one pointer: the new value, or `undefined` to remove the key.
const set = (pointer, value) => ({ pointer, value });

// ---------------------------------------------------------------- MCP servers

/// The deny entries for one server row: its name, and its URL or command.
export function serverEntries(row) {
    const entries = [{ serverName: row.name }];
    if (row.url) entries.push({ serverUrl: row.url });
    if (row.command?.length) entries.push({ serverCommand: row.command });
    return entries;
}

function matchesServer(entry, row) {
    return entry.serverName === row.name
        || (row.url && entry.serverUrl === row.url)
        || (row.command?.length && sameList(entry.serverCommand, row.command));
}

/// Servers already blocked in a policy document, as decisions keyed by row id.
export function serversBlockedToday(document, rows) {
    const today = valueAt(document ?? {}, "/deniedMcpServers") ?? [];
    return Object.fromEntries(rows.filter((row) => today.some((entry) => matchesServer(entry, row))).map((row) => [row.id, "block"]));
}

function deniedServersAfter(document, rows, decisions) {
    const today = valueAt(document ?? {}, "/deniedMcpServers") ?? [];
    const unblocked = rows.filter((row) => decisions[row.id] !== "block");
    const kept = today.filter((entry) => !unblocked.some((row) => matchesServer(entry, row)));
    const added = marked(decisions, "block").flatMap((id) => {
        const row = rows.find((r) => r.id === id) ?? { name: id };
        return serverEntries(row);
    });
    return unique([...kept, ...added]);
}

const MCP_BLOCK = {
    id: "mcp-block",
    title: "Block an MCP server",
    goal: "Stop chosen MCP servers from loading. Every other server keeps working, including servers people install later.",
    tradeoff: "A block applies to the selected server identities. It does not stop a server that a person runs under a different name and a different command or URL.",
    support: {
        [CLAUDE]: { level: "yes", via: ["/deniedMcpServers", "/disableClaudeAiConnectors"] },
        [COPILOT]: { level: "yes", via: ["/deniedMcpServers"] },
        [CODEX]: { level: "no", why: "The current managed Codex catalog supports an MCP allowlist, not this narrow server block. Review allowlist scope in Agent Policy." },
    },
    steps: [
        {
            id: "servers",
            card: "decisions",
            scope: "all",
            question: "Which MCP servers should be blocked?",
            help: "Servers your endpoints called in the last 30 days, by the name in their tool calls. A block matches that server name.",
            source: "mcp_servers",
            decisions: [{ id: "block", label: "Block", danger: true }],
            addByName: "Block a server by name",
            start: ({ today, rows }) => ({ ...serversBlockedToday(today[CLAUDE], rows), ...serversBlockedToday(today[COPILOT], rows) }),
            writesPointers: { [CLAUDE]: ["/deniedMcpServers"], [COPILOT]: ["/deniedMcpServers"] },
            model: {
                mayInfer: "Server names from a description, such as the paste one.",
                mustConfirm: "A block on a server that more than 10 endpoints use.",
                outOfScope: { "allow only listed servers": "Codex allowlists and Claude Code allowlists are not part of this change." },
            },
            examples: [
                { say: "block paste-share and the remote shell one", answer: { "paste-share": "block", "remote-shell": "block" } },
                { say: "unblock paste-share", answer: { "paste-share": null } },
            ],
        },
        {
            id: "connectors",
            card: "check",
            scope: CLAUDE,
            question: "Also block every claude.ai connector?",
            help: "Connectors that people add in claude.ai are not in the server list. This blocks all of them, approved or not.",
            writesPointers: { [CLAUDE]: ["/disableClaudeAiConnectors"] },
            model: { mayInfer: "Yes or no from the reply.", mustConfirm: null, outOfScope: {} },
            examples: [{ say: "yes, block connectors too", answer: true }],
        },
    ],
    writes(answers, agentId, document, rows) {
        if (agentId === CODEX) return [];
        const out = [];
        const before = valueAt(document ?? {}, "/deniedMcpServers");
        const after = deniedServersAfter(document, rows, answers.servers ?? {});
        if (!sameList(before, after)) out.push(set("/deniedMcpServers", after.length ? after : undefined));
        if (agentId === CLAUDE && answers.connectors === true && valueAt(document ?? {}, "/disableClaudeAiConnectors") !== true) {
            out.push(set("/disableClaudeAiConnectors", true));
        }
        return out;
    },
    impact(answers, rows, { today, inScope }) {
        const out = [];
        for (const agentId of inScope.filter((id) => id !== CODEX)) {
            const blocked = serversBlockedToday(today[agentId], rows);
            const newly = marked(answers.servers, "block").map((id) => rows.find((r) => r.id === id)).filter((r) => r && !blocked[r.id]);
            if (newly.length) out.push({ tone: "warn", text: `${AGENT_LABELS[agentId]}: ${newly.length} newly restricted server names. Endpoint populations can overlap; a distinct affected-endpoint count is unavailable. Reported model usage does not reliably identify the AI Agent application. Validate the effect in the pilot.` });
        }
        return out;
    },
    effects(answers, agentId) {
        const out = [];
        const names = marked(answers.servers, "block");
        if (names.length) out.push(`${names.join(", ")} never load, from any settings source.`);
        if (agentId === CLAUDE && answers.connectors === true) out.push("No claude.ai connector loads.");
        out.push("Every other server keeps working, including servers people install later.");
        return out;
    },
    pilotChecks: ["Tool calls to a blocked server stop on the pilot endpoints after their next session start."],
};

// ---------------------------------------------------------------- commands

const LIST_FOR = { allow: "/permissions/allow", ask: "/permissions/ask", deny: "/permissions/deny" };
const NO_NEVER = ["on-request", "untrusted", "granular"];

export const DECISION_LABELS = Object.freeze({ allow: "Run", ask: "Ask", deny: "Block" });

/// The kinds of rule the rules step takes, and the decisions each allows.
/// Agents read a file rule for reading and editing alike, so a file rule
/// cannot run without approval.
export const RULE_KINDS = Object.freeze({
    command: { label: "Command", placeholder: "terraform apply", decisions: ["allow", "ask", "deny"] },
    files: { label: "Files or folders", placeholder: "~/.aws", decisions: ["ask", "deny"] },
    mcp: { label: "MCP tool", placeholder: "mcp__server__tool", decisions: ["allow", "ask", "deny"] },
});

/// What a rule of each kind does not match.
export const RULE_LIMITS = Object.freeze({
    command: "A command run by its full path, such as /usr/bin/curl, or inside sh -c, is not matched.",
    files: "Programs that open the file themselves, such as a Python script, are not matched. A command that uses the file, such as aws with ~/.aws, still works; add a rule for the command too.",
    mcp: "",
});

/// A path as Claude Code's file rules write it: forward slashes, `//` for an
/// absolute path, and a Windows drive as `//c/`. A bare `/path` would be read
/// relative to the settings file.
function claudePath(path) {
    let p = String(path).trim().replace(/\\/g, "/").replace(/\/+$/, "");
    const drive = /^([A-Za-z]):\//.exec(p);
    if (drive) p = `//${drive[1].toLowerCase()}/${p.slice(3)}`;
    else if (p.startsWith("/") && !p.startsWith("//")) p = `/${p}`;
    return p;
}
const copilotPath = (path) => String(path).trim().replace(/\\/g, "/").replace(/\/+$/, "");
const withChildren = (p) => (p.includes("*") ? [p] : [p, `${p}/**`]);

/// The permission rules one entry writes for Claude Code or GitHub Copilot.
/// A Claude Code command rule has a PowerShell twin: on Windows a Bash deny
/// rule with no PowerShell rule beside it turns the PowerShell tool off, and
/// with the twin the rule holds in both shells.
export function ruleStrings(entry, agentId) {
    const t = String(entry?.target ?? "").trim();
    if (!t) return [];
    const star = t.includes("*");
    if (agentId === CLAUDE) {
        switch (entry.kind) {
            case "command": return [star ? `Bash(${t})` : `Bash(${t}:*)`, star ? `PowerShell(${t})` : `PowerShell(${t} *)`, ...(entry.fullPaths ? [`Bash(*/${t} *)`] : [])];
            case "powershell": return [`PowerShell(${t} *)`];
            case "files": { const paths = withChildren(claudePath(t)); return [...paths.map((p) => `Read(${p})`), ...paths.map((p) => `Edit(${p})`)]; }
            case "edit": return [`Edit(${claudePath(t)})`];
            case "mcp": return [t];
            default: return [];
        }
    }
    if (agentId === COPILOT) {
        switch (entry.kind) {
            case "command": return [...(star ? [`Shell(${t})`] : [`Shell(${t})`, `Shell(${t} *)`]), ...(entry.fullPaths ? [`Shell(*/${t} *)`] : [])];
            case "powershell": return [`Shell(${t})`, `Shell(${t} *)`];
            case "files": { const paths = withChildren(copilotPath(t)); return [...paths.map((p) => `Read(${p})`), ...paths.map((p) => `Write(${p})`)]; }
            case "edit": return [`Write(${copilotPath(t)})`];
            default: return [];
        }
    }
    return [];
}

/// The first words a Codex prefix rule matches for one entry, or null. Codex
/// rules match a command's first words only, so files, MCP tools and
/// patterns with * have no Codex rule.
export function codexPrefix(entry) {
    const t = String(entry?.target ?? "").trim();
    if ((entry?.kind !== "command" && entry?.kind !== "powershell") || !t || t.includes("*")) return null;
    return t.split(/\s+/).filter(Boolean);
}
const codexRule = (tokens, decision) => ({ pattern: tokens.map((token) => ({ token })), decision });

/// The rules each agent gets for a set of entries with one decision, as
/// people read them.
export function agentRulesText(entries, decision) {
    const claude = entries.flatMap((e) => ruleStrings(e, CLAUDE));
    const copilot = entries.flatMap((e) => ruleStrings(e, COPILOT));
    const codex = entries.map(codexPrefix).filter(Boolean).map((t) => t.join(" "));
    const commands = entries.some((e) => e.kind === "command" || e.kind === "powershell");
    const codexText = !codex.length ? (commands ? "No rule. Codex rules match a command's first words, with no *." : "No rule. Codex rules cover commands only.")
        : decision === "allow" ? "No rule. Codex cannot run a command without approval."
            : `${codex.join(", ")}: ${decision === "deny" ? "forbidden" : "prompt"}`;
    return [
        { agentId: CLAUDE, text: claude.join(", ") || "No rule." },
        { agentId: COPILOT, text: copilot.length ? copilot.join(", ") : "No rule. GitHub Copilot has no rule for one MCP tool." },
        { agentId: CODEX, text: codexText },
    ];
}

/// The rule entries the answers choose: the person's own rules, or the rules
/// of the Triage case groups they decided on, by the first step's answer. An
/// entry with no decision removes its rules.
export function ruleEntries(answers, rows = []) {
    // A session saved before the rules step kept a map of command to decision.
    if (answers?.commands) return Object.entries(answers.commands).map(([target, decision]) => ({ kind: "command", target, decision }));
    if (answers?.known === "no") {
        return Object.entries(answers.cases ?? {}).flatMap(([id, decision]) => (rows.find((r) => r.id === id && !r.noRule)?.rules ?? []).map((rule) => ({ ...rule, decision })));
    }
    return (answers?.rules ?? []).filter((e) => String(e.target ?? "").trim());
}

function permissionListsAfter(document, entries, agentId) {
    // Every rule an entry names is owned by this change: it leaves every
    // list, then returns to the list of the entry's decision.
    const owned = new Set(entries.flatMap((e) => ruleStrings(e, agentId)));
    const out = [];
    for (const [decision, pointer] of Object.entries(LIST_FOR)) {
        const today = valueAt(document ?? {}, pointer) ?? [];
        const kept = today.filter((rule) => !owned.has(rule));
        const after = unique([...kept, ...entries.filter((e) => e.decision === decision).flatMap((e) => ruleStrings(e, agentId))]);
        if (!sameList(today, after)) out.push(set(pointer, after.length ? after : undefined));
    }
    return out;
}

const CLAUDE_RULE = /^Bash\((.+?)(?::\*)?\)$/;
const COPILOT_RULE = /^(?:Shell|Bash)\((.+?)(?: \*)?\)$/;

/// Command prefixes and their decision in today's policies. A prefix whose
/// agents disagree is left without a decision.
export function commandsToday(today) {
    const found = new Map();
    const note = (prefix, decision) => {
        const key = prefix.trim();
        if (!key || key.includes("*")) return;
        found.set(key, found.has(key) && found.get(key) !== decision ? null : decision);
    };
    for (const [decision, pointer] of Object.entries(LIST_FOR)) {
        for (const rule of valueAt(today[CLAUDE] ?? {}, pointer) ?? []) { const m = CLAUDE_RULE.exec(rule); if (m) note(m[1], decision); }
        for (const rule of valueAt(today[COPILOT] ?? {}, pointer) ?? []) { const m = COPILOT_RULE.exec(rule); if (m) note(m[1], decision); }
    }
    for (const rule of valueAt(today[CODEX] ?? {}, "/rules/prefix_rules") ?? []) {
        const tokens = (rule.pattern ?? []).map((part) => part.token);
        if (tokens.every(Boolean)) note(tokens.join(" "), rule.decision === "forbidden" ? "deny" : "ask");
    }
    return Object.fromEntries([...found].filter(([, d]) => d));
}

/// The rules step's starting rows: the command rules in today's policies.
export const rulesToday = (today) => Object.entries(commandsToday(today)).map(([target, decision]) => ({ kind: "command", target, decision, today: true, todayDecision: decision }));

const RULE_POINTERS = {
    [CLAUDE]: ["/permissions/allow", "/permissions/ask", "/permissions/deny"],
    [COPILOT]: ["/permissions/allow", "/permissions/ask", "/permissions/deny"],
    [CODEX]: ["/rules/prefix_rules"],
};
const OUT_OF_SCOPE = { "network domains": "Domain rules are not part of this change." };

const COMMANDS = {
    id: "commands",
    title: "Decide which commands run without approval",
    goal: "Let routine commands run without a prompt, ask before risky ones, and block dangerous ones.",
    tradeoff: "Each command that runs without approval saves a prompt and lets an agent run it with no person watching. Each block stops work that needs the command.",
    support: {
        [CLAUDE]: { level: "yes", via: ["/permissions/allow", "/permissions/ask", "/permissions/deny", "/permissions/disableBypassPermissionsMode", "/allowManagedPermissionRulesOnly"] },
        [COPILOT]: { level: "yes", via: ["/permissions/allow", "/permissions/ask", "/permissions/deny", "/permissions/disableBypassPermissionsMode"] },
        [CODEX]: { level: "partly", why: "Codex rules can ask or block a command by its first words. They cannot let a command run without approval, and they do not cover files or MCP tools.", via: ["/rules/prefix_rules", "/allowed_approval_policies"] },
    },
    steps: [
        {
            id: "known",
            card: "choice",
            scope: "all",
            question: "Do you know which tool calls should ask first or be blocked?",
            options: [
                { id: "yes", label: "Yes, I will name them", detail: "Commands, files or folders, or MCP tools. You can also describe them below." },
                { id: "no", label: "No, start from Triage cases", detail: "The tool calls cited by the cases in Triage in the last 30 days." },
            ],
            writesPointers: {},
            model: { mayInfer: "Yes when the person names a command, file, or tool; no when they ask what to restrict or want suggestions.", mustConfirm: null, outOfScope: {} },
            examples: [{ say: "I want to block terraform destroy", answer: "yes" }, { say: "not sure, what do you suggest?", answer: "no" }],
        },
        {
            id: "rules",
            card: "rules",
            scope: "all",
            when: (answers) => answers.known === "yes",
            question: "Which tool calls should ask first or be blocked?",
            help: "One rule for each command, file or folder, or MCP tool. A command rule covers every command that starts with these words. Today's command rules are listed first.",
            start: ({ today }) => rulesToday(today),
            writesPointers: RULE_POINTERS,
            model: {
                mayInfer: "Rules from a description: the kind, the command's first words or the path, and the decision, such as a block on reads of ~/.aws.",
                mustConfirm: "Run without approval for a command that changes remote systems, such as git push or curl.",
                outOfScope: OUT_OF_SCOPE,
            },
            examples: [
                { say: "keep agents out of ~/.aws", answer: [{ kind: "files", target: "~/.aws", decision: "deny" }] },
                { say: "ask before terraform apply, and git status can just run", answer: [{ kind: "command", target: "terraform apply", decision: "ask" }, { kind: "command", target: "git status", decision: "allow" }] },
            ],
        },
        {
            id: "cases",
            card: "themes",
            scope: "all",
            when: (answers) => answers.known === "no",
            source: "triage_cases",
            question: "Which of these should ask first or be blocked?",
            help: "Tool calls the Triage cases cite, grouped by the rule that covers them. The auto mode classifier already stops most destructive commands.",
            decisions: [{ id: "ask", label: "Ask" }, { id: "deny", label: "Block", danger: true }],
            writesPointers: RULE_POINTERS,
            model: {
                mayInfer: "Decisions for groups from a description, such as a block on merges.",
                mustConfirm: "Block on a group whose commands people use for routine work, such as git push.",
                outOfScope: OUT_OF_SCOPE,
            },
            examples: [{ say: "block the merges and ask before secret store reads", answer: { merge: "deny", "secret-store": "ask" } }],
        },
        {
            id: "bypass",
            card: "choice",
            scope: "all",
            question: "Should people be able to skip approvals for a whole session?",
            help: "Every agent has a mode that runs every command without asking.",
            options: [
                { id: "off", label: "Turn off modes that skip approvals", recommended: true, perAgent: { [CLAUDE]: "--dangerously-skip-permissions is refused", [COPILOT]: "Allow-all and yolo modes are refused", [CODEX]: "The never-ask approval policy is refused" } },
                { id: "leave", label: "Leave unchanged" },
            ],
            writesPointers: { [CLAUDE]: ["/permissions/disableBypassPermissionsMode"], [COPILOT]: ["/permissions/disableBypassPermissionsMode"], [CODEX]: ["/allowed_approval_policies"] },
            model: { mayInfer: "Off or leave from the reply.", mustConfirm: null, outOfScope: {} },
            examples: [{ say: "no yolo mode", answer: "off" }],
        },
        {
            id: "policy_rules_only",
            card: "check",
            scope: CLAUDE,
            question: "Use only the rules in this policy?",
            help: "Permission rules in user and project settings stop applying, including their block rules.",
            writesPointers: { [CLAUDE]: ["/allowManagedPermissionRulesOnly"] },
            model: { mayInfer: "Yes or no from the reply.", mustConfirm: null, outOfScope: {} },
            examples: [{ say: "ignore people's own rules", answer: true }],
        },
    ],
    writes(answers, agentId, document, rows) {
        const entries = ruleEntries(answers, rows);
        const out = [];
        if (agentId === CLAUDE || agentId === COPILOT) {
            out.push(...permissionListsAfter(document, entries, agentId));
            if (answers.bypass === "off" && valueAt(document ?? {}, "/permissions/disableBypassPermissionsMode") !== "disable") {
                out.push(set("/permissions/disableBypassPermissionsMode", "disable"));
            }
            if (agentId === CLAUDE && answers.policy_rules_only === true && valueAt(document ?? {}, "/allowManagedPermissionRulesOnly") !== true) {
                out.push(set("/allowManagedPermissionRulesOnly", true));
            }
        }
        if (agentId === CODEX) {
            const today = valueAt(document ?? {}, "/rules/prefix_rules") ?? [];
            const tokens = (rule) => (rule.pattern ?? []).map((part) => part.token).join(" ");
            const owned = new Set(entries.map(codexPrefix).filter(Boolean).map((t) => t.join(" ")));
            const kept = today.filter((rule) => !owned.has(tokens(rule)));
            const added = entries.flatMap((e) => {
                const prefix = codexPrefix(e);
                if (!prefix || (e.decision !== "ask" && e.decision !== "deny")) return [];
                return [codexRule(prefix, e.decision === "ask" ? "prompt" : "forbidden")];
            });
            const after = unique([...kept, ...added]);
            if (!sameList(today, after)) out.push(set("/rules/prefix_rules", after.length ? after : undefined));
            if (answers.bypass === "off") {
                const policies = valueAt(document ?? {}, "/allowed_approval_policies");
                const next = (policies ?? NO_NEVER).filter((p) => p !== "never");
                if (!sameList(policies, next) && next.length) out.push(set("/allowed_approval_policies", next));
            }
        }
        return out;
    },
    impact(answers, rows, { inScope }) {
        const entries = ruleEntries(answers, rows);
        const withDecision = (d) => entries.filter((e) => e.decision === d);
        const out = [];
        if (withDecision("deny").length) out.push({ tone: "warn", text: "A blocked tool call returns an error to the agent, which continues the task without it." });
        const allowed = withDecision("allow").filter((e) => codexPrefix(e));
        if (allowed.length && inScope.includes(CODEX)) out.push({ tone: "info", text: `Codex: ${count(allowed.length, "command marked Run keeps", "commands marked Run keep")} the approval behavior of the person's Codex settings.` });
        if (inScope.includes(COPILOT) && entries.some((e) => e.kind === "mcp" && e.decision)) out.push({ tone: "info", text: "GitHub Copilot: MCP tool rules are not written. GitHub Copilot has no rule for one MCP tool." });
        const codexMiss = entries.filter((e) => e.decision && !codexPrefix(e)).length;
        if (inScope.includes(CODEX) && codexMiss) out.push({ tone: "info", text: `Codex: ${count(codexMiss, "rule is", "rules are")} not written. Codex rules match a command's first words only, so files, MCP tools, and patterns with * have no Codex rule.` });
        if (answers.bypass === "off") out.push({ tone: "warn", text: "People who use these modes today get approval prompts again." });
        return out;
    },
    effects(answers, agentId, rows = []) {
        const entries = ruleEntries(answers, rows).filter((e) => (agentId === CODEX ? codexPrefix(e) && e.decision !== "allow" : ruleStrings(e, agentId).length));
        const n = (d) => entries.filter((e) => e.decision === d).length;
        const out = [];
        if (n("allow")) out.push(`${count(n("allow"), "tool call runs", "tool calls run")} without a prompt.`);
        if (n("ask")) out.push(`${count(n("ask"), "tool call always asks", "tool calls always ask")} first.`);
        if (n("deny")) out.push(`${count(n("deny"), "tool call is", "tool calls are")} refused.`);
        if (answers.bypass === "off") out.push("No session can skip approvals.");
        if (agentId === CLAUDE && answers.policy_rules_only === true) out.push("Only this policy's permission rules apply.");
        return out;
    },
    pilotChecks: ["Blocked tool calls stop appearing in the pilot endpoints' tool calls."],
};

// ---------------------------------------------------------------- features

// `about` says what each agent's switch does, from the agent's own source or
// docs. The chat model describes a feature only from this text.
const CODEX_SRC = "Codex source, features/src/lib.rs, checkout of 2026-08-21";

/// An about text as people read it: without the source citation the model
/// gets.
export const aboutForPeople = (text) => String(text ?? "").replace(` (${CODEX_SRC})`, "");

/// Every feature the change can turn off. A row lists only the agents that
/// have the feature; each agent's step shows its own rows, in this order.
export const FEATURE_ROWS = Object.freeze([
    {
        id: "remote", label: "Remote control from other devices",
        what: "A person controls a running agent session from another device, such as a phone or a browser.",
        map: { [CLAUDE]: ["/disableRemoteControl", true], [CODEX]: ["/allow_remote_control", false], [COPILOT]: ["/remoteControl", { mode: "disabled" }] },
        about: { [CLAUDE]: "claude remote-control and --remote are refused.", [CODEX]: "Remote Control in the Codex app-server is turned off.", [COPILOT]: "A Copilot CLI session cannot be controlled from another device." },
    },
    {
        id: "browser", label: "Agent actions on external web pages",
        what: "The agent opens, reads, and acts on websites in a browser.",
        map: { [CLAUDE]: ["/browserExternalPageTools", "disabled"], [CODEX]: ["/features/browser_use_external", false] },
        about: { [CLAUDE]: "Claude's tools cannot act on external pages in the Claude Desktop browser pane. Claude Desktop only.", [CODEX]: `Browser Use integration with external browsers is off. A requirements-only switch (${CODEX_SRC}).` },
    },
    {
        id: "websearch", label: "Web search",
        what: "The agent searches the web and reads the results.",
        map: { [CLAUDE]: ["/permissions/deny", ["WebSearch"]], [CODEX]: ["/allowed_web_search_modes", ["disabled"]] },
        about: { [CLAUDE]: "A deny rule blocks the WebSearch tool.", [CODEX]: "Only the disabled web search mode can be selected." },
    },
    {
        id: "multi", label: "Multi-agent workflows",
        what: "One task starts more agents that work on parts of it at the same time.",
        map: { [CLAUDE]: ["/disableWorkflows", true], [CODEX]: ["/features/multi_agent", false] },
        about: { [CLAUDE]: "Dynamic workflows, which start many subagents for one task, are not offered.", [CODEX]: `The collab tools are off (${CODEX_SRC}).` },
    },
    {
        id: "publish", label: "Publishing output to a web page",
        what: "The agent publishes its output to a hosted web page.",
        map: { [CLAUDE]: ["/disableArtifact", true] },
        about: { [CLAUDE]: "The Artifact tool, which publishes output to a hosted web page, is off." },
    },
    {
        id: "bundled_skills", label: "Skills that ship with Claude Code",
        what: "The agent loads the skills that come with the Claude Code CLI.",
        map: { [CLAUDE]: ["/disableBundledSkills", true] },
        about: { [CLAUDE]: "Skills that ship with the Claude Code CLI are not loaded." },
    },
    {
        id: "skill_shell", label: "Shell commands inside skills",
        what: "The agent runs shell commands that are written inside a skill.",
        map: { [CLAUDE]: ["/disableSkillShellExecution", true] },
        about: { [CLAUDE]: "Inline shell commands inside skills do not run. A skill that depends on them stops working." },
    },
    {
        id: "computer", label: "Computer use",
        what: "The agent sees the screen and uses the mouse and keyboard.",
        map: { [CODEX]: ["/features/computer_use", false] },
        about: { [CODEX]: `Codex Computer Use is off. A requirements-only switch (${CODEX_SRC}).` },
    },
    {
        id: "memories", label: "Memories across sessions",
        what: "The agent keeps notes from earlier sessions and uses them in new ones.",
        map: { [CODEX]: ["/features/memories", false] },
        about: { [CODEX]: `Startup memory extraction and file-backed memory consolidation are off (${CODEX_SRC}).` },
    },
    {
        id: "images", label: "Image generation",
        what: "The agent creates images.",
        map: { [CODEX]: ["/features/image_generation", false] },
        about: { [CODEX]: `Extension-backed image generation is off (${CODEX_SRC}).` },
    },
    {
        id: "fast_mode", label: "Fast mode",
        what: "A person selects a faster mode for the agent's replies.",
        map: { [CODEX]: ["/features/fast_mode", false] },
        about: { [CODEX]: `People cannot select Fast mode in the TUI and request layer (${CODEX_SRC}).` },
    },
    {
        id: "goals", label: "Goals",
        what: "The agent keeps a goal for a thread and continues toward it without a new prompt.",
        map: { [CODEX]: ["/features/goals", false] },
        about: { [CODEX]: `Persisted thread goals and automatic goal continuation are off (${CODEX_SRC}).` },
    },
    {
        id: "worktrees", label: "Worktrees",
        what: "The agent works on a task in a separate Git worktree.",
        map: { [CODEX]: ["/features/worktrees", false] },
        about: { [CODEX]: "The Codex source Origin has read does not describe this feature." },
    },
    {
        id: "plugins", label: "Plugins",
        what: "The agent loads plugins that people install.",
        map: { [CODEX]: ["/features/plugins", false] },
        about: { [CODEX]: `Plugins are off (${CODEX_SRC}).` },
    },
    {
        id: "apps", label: "Apps",
        what: "The agent uses apps that are connected to the person's account.",
        map: { [CODEX]: ["/features/apps", false] },
        about: { [CODEX]: `Apps are off (${CODEX_SRC}).` },
    },
    {
        id: "realtime_conversation", label: "Voice conversations",
        what: "A person talks with the agent by voice in the terminal.",
        map: { [CODEX]: ["/features/realtime_conversation", false] },
        about: { [CODEX]: `The experimental realtime voice conversation mode in the TUI is off (${CODEX_SRC}).` },
    },
]);
const LIST_POINTERS = new Set(["/permissions/deny"]);

/// The feature rows one agent has.
export const featureRowsFor = (agentId) => FEATURE_ROWS.filter((row) => row.map[agentId]);

/// The id of the step that asks about one agent's features.
export const featureStepId = (agentId) => `${agentId.replace(/-/g, "_")}_features`;

/// True when an agent's policy turns a feature row off today.
export function featureOffToday(document, row, agentId) {
    const m = row.map[agentId];
    if (!m) return false;
    const today = valueAt(document ?? {}, m[0]);
    if (LIST_POINTERS.has(m[0])) return m[1].every((entry) => (today ?? []).some((t) => JSON.stringify(t) === JSON.stringify(entry)));
    return JSON.stringify(today) === JSON.stringify(m[1]);
}

const featureStep = (agentId, examples) => ({
    id: featureStepId(agentId),
    card: "features",
    scope: agentId,
    question: `Which ${AGENT_LABELS[agentId]} features should be off?`,
    help: "Off: the policy turns the feature off, and people cannot turn it back on. On: the policy does not turn it off.",
    rows: featureRowsFor(agentId),
    start: ({ today }) => Object.fromEntries(featureRowsFor(agentId).map((row) => [row.id, featureOffToday(today[agentId], row, agentId) ? "off" : "on"])),
    writesPointers: { [agentId]: featureRowsFor(agentId).map((row) => row.map[agentId][0]) },
    model: { mayInfer: "Feature rows from a description, such as no browsing.", mustConfirm: null, outOfScope: { "force a feature on": "On only removes this policy's off setting. People can still turn the feature off for themselves." } },
    examples,
});

const FEATURES = {
    id: "features-off",
    title: "Turn agent features off",
    goal: "Turn off remote control, browser use, web search, memories and other built-in features.",
    tradeoff: "Each feature that is off removes one way for an agent to reach outside the machine, and removes a tool some people use.",
    support: {
        [CLAUDE]: { level: "yes", via: featureRowsFor(CLAUDE).map((row) => row.map[CLAUDE][0]) },
        [CODEX]: { level: "yes", via: ["/allow_remote_control", "/features", "/allowed_web_search_modes"] },
        [COPILOT]: { level: "partly", why: "Only remote control. Copilot feature switches are not available yet.", via: ["/remoteControl"] },
    },
    steps: [
        featureStep(CLAUDE, [{ say: "no remote control and no web search", answer: { remote: "off", websearch: "off" } }]),
        featureStep(CODEX, [{ say: "no voice and no computer use", answer: { realtime_conversation: "off", computer: "off" } }]),
        featureStep(COPILOT, [{ say: "no remote control", answer: { remote: "off" } }]),
    ],
    writes(answers, agentId, document) {
        const out = new Map();
        const put = (pointer, value) => {
            if (LIST_POINTERS.has(pointer)) {
                const base = out.get(pointer) ?? valueAt(document ?? {}, pointer) ?? [];
                const next = unique([...base, ...value]);
                if (!sameList(valueAt(document ?? {}, pointer), next)) out.set(pointer, next);
                return;
            }
            if (JSON.stringify(valueAt(document ?? {}, pointer)) !== JSON.stringify(value)) out.set(pointer, value);
        };
        const drop = (pointer, value) => {
            if (LIST_POINTERS.has(pointer)) {
                const base = out.get(pointer) ?? valueAt(document ?? {}, pointer) ?? [];
                const next = base.filter((entry) => !value.some((v) => JSON.stringify(v) === JSON.stringify(entry)));
                if (!sameList(valueAt(document ?? {}, pointer), next)) out.set(pointer, next.length ? next : undefined);
                return;
            }
            if (JSON.stringify(valueAt(document ?? {}, pointer)) === JSON.stringify(value)) out.set(pointer, undefined);
        };
        const picked = answers[featureStepId(agentId)] ?? {};
        for (const row of featureRowsFor(agentId)) {
            const [pointer, value] = row.map[agentId];
            if (picked[row.id] === "off") put(pointer, value);
            if (picked[row.id] === "on") drop(pointer, value);
        }
        return [...out].map(([pointer, value]) => set(pointer, value));
    },
    impact(answers, rows, { today = {}, inScope = AGENT_IDS } = {}) {
        const out = [];
        for (const agentId of inScope) {
            const picked = answers[featureStepId(agentId)] ?? {};
            for (const row of featureRowsFor(agentId)) {
                if (picked[row.id] === "on" && featureOffToday(today[agentId], row, agentId)) {
                    out.push({ tone: "warn", text: `${row.label} is off today for ${AGENT_LABELS[agentId]}. On removes that setting.` });
                }
            }
        }
        if (inScope.includes(CLAUDE) && answers[featureStepId(CLAUDE)]?.browser === "off") out.push({ tone: "info", text: "Claude Code: agent actions on external web pages apply in Claude Desktop only. The CLI has no browser pane." });
        return out;
    },
    effects(answers, agentId) {
        const picked = answers[featureStepId(agentId)] ?? {};
        const names = featureRowsFor(agentId).filter((row) => picked[row.id] === "off").map((row) => row.label.toLowerCase());
        return names.length ? [`Off: ${names.join(", ")}.`] : [];
    },
    pilotChecks: ["The turned-off features are missing on the pilot endpoints after their next session start."],
};

const GUIDANCE = {
    id: "reviewer-guidance", title: "Add context for automatic review",
    goal: "Give the automatic reviewer investigation-specific context before it approves actions.",
    tradeoff: "Guidance influences the reviewer when it runs. It is not a deterministic tool block and does not enable automatic review by itself.",
    support: {
        [CLAUDE]: { level: "yes", via: ["/autoMode/environment"] },
        [CODEX]: { level: "yes", via: ["/guardian_extra_policy"] },
        [COPILOT]: { level: "no", why: "No equivalent reviewer guidance control is mapped in this workflow." },
    },
    steps: [{ examples: [{ say: "Ask before weakening production database consistency.", answer: "Ask before weakening production database consistency." }], id: "guidance", scope: "all", card: "text", question: "What should the automatic reviewer consider?",
        help: "Describe the concern, the context that distinguishes legitimate work, and when a person should review it. Existing managed guidance is preserved.",
        writesPointers: { [CLAUDE]: ["/autoMode/environment"], [CODEX]: ["/guardian_extra_policy"] },
        model: { mayInfer: "Suggest wording from the investigation evidence.", mustConfirm: "The exact guidance text and affected AI Agent types.", outOfScope: { prevention: "Do not promise prevention or enable auto mode." } },
    }],
    writes(answers, agentId, document) {
        const text = String(answers.guidance || "").trim();
        if (!text || text.length > 4000) return [];
        if (agentId === CLAUDE) {
            const before = valueAt(document, "/autoMode/environment") || [];
            return before.includes(text) ? [] : [set("/autoMode/environment", [...before, text])];
        }
        if (agentId === CODEX) {
            const before = valueAt(document, "/guardian_extra_policy") || "";
            return `\n\n${before}\n\n`.includes(`\n\n${text}\n\n`) ? [] : [set("/guardian_extra_policy", [before, text].filter(Boolean).join("\n\n"))];
        }
        return [];
    },
    impact() { return [{ tone: "warn", text: "No historical prevention estimate is available for reviewer guidance. Codex managed extra policy replaces the user's own extra reviewer text. Test legitimate work as well as the case behavior during the pilot." }]; },
    effects(_answers, agentId) { return [agentId === CODEX ? "Adds to the managed extra reviewer policy. User extra policy text is replaced." : "Adds context to Claude Code auto mode's environment guidance."]; },
    pilotChecks: ["Confirm the reviewer receives the guidance when automatic review runs.", "Try the concerning action and a legitimate lookalike; record the actual decisions."],
};
export const CHANGES = Object.freeze([GUIDANCE, MCP_BLOCK, COMMANDS, FEATURES]);
export const writePermissionRules = COMMANDS.writes;
export const appendReviewerGuidance = GUIDANCE.writes;
export const writeMcpServerBlocks = MCP_BLOCK.writes;

export const changeById = (id) => CHANGES.find((change) => change.id === id) ?? null;

/// Agents a change can write, in display order.
export const supportedAgents = (change) => AGENT_IDS.filter((agentId) => change.support[agentId].level !== "no");
